/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../../../platform/Platform';
import { ComponentManagerFn } from '../../../../registry/Component.type';
import { baseComponentManager } from '../../../../registry/Components';
import { baseColumnProps } from '../BaseColumn';
import { defaultValue } from './FormulaNumberColumn';

const FormulaNumberColumnManager: ComponentManagerFn<'FormulaNumberColumn'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'FormulaNumberColumn'>(
      platform,
      'FormulaNumberColumn',
    ),
    type: 'FormulaNumberColumn',
    name: 'Formula Number Column',
    isChildAllowed: (page, item, childType) => false,
    defaultValue,
    props: {
      ...baseColumnProps,
      index: {
        label: 'Index',
        type: 'number',
      },
      formula: {
        label: 'Formula',
        type: 'expression',
        returnType: async () => 'number',
      },
    },
    getDisplayText: (item) =>
      typeof item.props.label === 'string'
        ? item.props.label
        : item.props.label.value ?? '',
    category: 'Data Grid',
    icon: { iconName: 'sigma' },
    description: `**Overview**<br/>
The Formula Number Column is a read-only Data Grid column that calculates its value dynamically based on other data in the same row.
Think of this like a formula cell in Excel. Instead of storing a static value in the database, this column executes a mathematical expression in the browser to derive a result on the fly. It is commonly used for line-item calculations like Total = Price * Quantity or Margin = (Sales - Cost) / Sales.

**1. Core Logic (The Formula)**<br/>
The power of this column lies entirely in the expression you define.
* **Formula:** A numeric expression that runs for every row in the grid.
* **Context:** You can access other columns in the specific row using the row context variable (typically \`$._\` or \`$.row\`).
    * **Example:** \`$._.unitPrice * $._.quantity\`
* **Behavior:** If the user edits the "Quantity" or "Unit Price" column, this Formula Number Column updates instantly without needing a server round-trip.

**2. Configuration**<br/>
* **Label:** The header text for the column (e.g., "Line Total").
* **Index:** Controls the visual position of the column if auto-layout is disabled.
* **View Attribute (Optional):** While mostly used for display, you can bind this to a View Attribute if you want to use standard column formatting (like currency or decimal precision) associated with that attribute type, though the data source itself is the formula, not the store.

**3. Usage Guidelines**<br/>
* **Read-Only:** Users cannot manually type into a Formula Number Column. Its value is strictly derived.
* **Client-Side Only:** These calculations happen in the user's browser. If you need complex business logic that requires database lookups or secure calculations (e.g., Tax Rates based on geolocation), consider using a Server-Side Computed Attribute instead.

**4. Best Use Cases**<br/>
* **Invoices:** Calculating Subtotal, Tax Amount, and Grand Total instantly as line items are added.
* **Scorecards:** Creating a weighted score based on multiple metric columns (e.g., \`(Quality * 0.4) + (Speed * 0.6)\`).
* **Unit Conversion:** Displaying a Distance (km) column when the database only stores Distance (miles).`,
  };
};

export default FormulaNumberColumnManager;
